import { useMemo } from 'react'
import { DateTime } from 'luxon'
import { OrderDetailsV3Query } from '~/__generated__/app/query/order-details/order-details.graphql'
import { CartTrainOrderTicketSeatDirection } from '~/__generated__/__types__'
import { isoToFormat } from '~/app/utils/useIsoToFormat'

import { transformTrainNo } from '~/api/utils/train'

/**
 * useTrainTicketData
 * author: soojin.oh
 */

// type definitions

// hook
type CategoryOrders = OrderDetailsV3Query['orderDetailsV3']['categoryOrders']
type TrainOrder = Extract<CategoryOrders[number], { __typename?: 'OrderDetailsTrain' }>
type Ticket = TrainOrder['tickets'][number]
const useTrainTicketData = (props: Ticket) => {
  const {
    departureAt,
    departureStationName,
    arrivalAt,
    arrivalStationName,
    trainNo,
    adults,
    child,
    goodsReservationNo,
    seats,
    stationTrainClassifyCode,
  } = props

  const departureTime = useMemo(
    () => DateTime.fromISO(departureAt).toFormat('HH:mm'),
    [departureAt]
  )
  const arrivalTime = useMemo(() => DateTime.fromISO(arrivalAt).toFormat('HH:mm'), [arrivalAt])
  const passengerInformation = useMemo(
    () => [adults && `성인 ${adults}`, child && `아동 ${child}`].filter(v => v).join(' · '),
    [adults, child]
  )
  const seatInformation = useMemo<[string, string, string][]>(
    () =>
      seats?.map(seat => [
        String(parseInt(seat.carriageNo)),
        seat.seatDescription,
        seat.direction === CartTrainOrderTicketSeatDirection.Forward ? '순방향' : '역방향',
      ]) || [],
    [seats]
  )
  const departureDate = useMemo(() => isoToFormat(departureAt, 'yyyy.MM.dd (ccc)'), [departureAt])
  const trainType = useMemo(() => {
    return transformTrainNo(trainNo)
  }, [trainNo])

  return {
    departureAt: departureTime,
    departureStationName,
    arrivalAt: arrivalTime,
    arrivalStationName,
    passengerInformation,
    trainType,
    goodsReservationNo,
    seatInformation,
    departureDate,
    stationTrainClassifyCode,
  }
}

export default useTrainTicketData
